import { phraseRepo, quickFireRepo } from '../../data/repositories';
import { availableLayouts } from '../profile/layouts';
import { getSettings } from '../profile/ProfileStore';
import { audioKeyFor, hasCachedAudio } from './audioCache';
import { keyEchoTexts } from './keyEcho';
import type { TtsProvider } from './TtsProvider';

/**
 * Render audio for stored text ahead of time, so the offline path has something to play.
 *
 * This is the mechanism behind the day-one audio decision: nothing is bundled at build time (no
 * Speech key in the build, no voice locked in while the family is still choosing between Leni and
 * Jan), so the cache is filled opportunistically whenever the app is online, plus on the
 * caregiver's explicit "Audio vorbereiten".
 *
 * The consequence is worth being honest about rather than hiding: a **freshly installed or restored
 * device has no audio** until it has been online once. The restore runbook says so, and
 * `missingAudioCount` exists so Betreuer-Modus can show "Audio fehlt" instead of leaving a
 * caregiver to discover it in a conversation.
 *
 * Quick fires come first, always. They are the utterances that carry the most conversational weight
 * per tap, and the ones a person will reach for when the network is gone.
 */

export interface PrerenderProgress {
  done: number;
  total: number;
}

export interface PrerenderResult {
  rendered: number;
  skipped: number;
  failed: number;
}

interface PrerenderItem {
  text: string;
  apply: (audioKey: string) => Promise<void>;
}

/**
 * The letter sounds key echo needs, across **every** shipped layout rather than the active one.
 *
 * Switching layout in care mode must not silence the echo until somebody renders again — and the four
 * layouts share almost every key, so the union costs a handful of extra items rather than four times
 * the work.
 *
 * The keycaps are passed through **exactly as the layout declares them**, which means uppercase. That
 * is not cosmetic: `speakKeyEcho` is called from `handleKey` with the raw keycap, so hashing a
 * lowercase letter here would produce a cache key no keypress ever looks up, and the echo would stay
 * silent with a full cache.
 */
function keyEchoItems(): PrerenderItem[] {
  const keys = new Set<string>();
  for (const layout of availableLayouts()) {
    for (const row of layout.rows) {
      for (const key of row) keys.add(key.key);
    }
  }

  // No repo row to stamp: a letter is not a phrase, it has no id and nothing reads an audioKey for it.
  // `audioKeyFor` is derived from the text, so the cache is the only record that needs to exist.
  return keyEchoTexts([...keys]).map((text) => ({ text, apply: async () => {} }));
}

async function collectItems(): Promise<PrerenderItem[]> {
  const [quickFires, phrases] = await Promise.all([quickFireRepo.all(), phraseRepo.all()]);

  return [
    ...quickFires.map((quickFire) => ({
      text: quickFire.text,
      apply: (audioKey: string) => quickFireRepo.setAudioKey(quickFire.id, audioKey),
    })),
    ...phrases.map((phrase) => ({
      text: phrase.text,
      apply: (audioKey: string) => phraseRepo.setAudioKey(phrase.id, audioKey),
    })),
    // Last, and only when the feature is on. Letters carry the least conversational weight per item,
    // so thirty-odd of them must never delay the phrases somebody needs when the network is gone —
    // and a caregiver whose person has echo off should not be told audio is missing for sounds that
    // will never be played.
    ...(getSettings().keyEchoEnabled ? keyEchoItems() : []),
  ];
}

/** How many stored utterances have no cached audio. Drives the care-mode warning. */
export async function missingAudioCount(voiceId: string): Promise<number> {
  const items = await collectItems();
  let missing = 0;
  for (const item of items) {
    if (!(await hasCachedAudio(await audioKeyFor(item.text, voiceId)))) missing += 1;
  }
  return missing;
}

/**
 * Render everything not yet cached, sequentially.
 *
 * Sequential on purpose: `/api/tts` rate-limits at 30 requests per minute per instance, and a
 * parallel burst over ~50 phrases would trip it and leave a partially rendered cache that looks
 * complete. Slower and complete beats faster and patchy for something the person relies on offline.
 */
export async function prerenderMissing(args: {
  provider: TtsProvider;
  voiceId: string;
  signal?: AbortSignal;
  onProgress?: (progress: PrerenderProgress) => void;
}): Promise<PrerenderResult> {
  const { provider, voiceId, signal, onProgress } = args;
  if (!provider.prerender) return { rendered: 0, skipped: 0, failed: 0 };

  const items = await collectItems();
  const result: PrerenderResult = { rendered: 0, skipped: 0, failed: 0 };

  for (const [index, item] of items.entries()) {
    if (signal?.aborted) break;

    const key = await audioKeyFor(item.text, voiceId);

    if (await hasCachedAudio(key)) {
      result.skipped += 1;
    } else {
      try {
        await provider.prerender(item.text, voiceId);
        await item.apply(key);
        result.rendered += 1;
      } catch (err) {
        result.failed += 1;
        // Metadata only, never the text (NFR7).
        console.warn('[prerender] failed for one item', { index, err: String(err).slice(0, 120) });
      }
    }

    onProgress?.({ done: index + 1, total: items.length });
  }

  return result;
}
